/* MyCertificates — small card listing the current learner's earned NHF
 * certificates. Each row has a Download button that streams the PDF and a
 * "Verify" link that opens the public /verify/<id> page in a new tab.
 *
 * Mounted at the bottom of /admin/help. Renders nothing when the learner
 * hasn't earned any certificates yet (no clutter for empty state).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Award, Download, ExternalLink, Loader2, Mail, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyCertificates = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/certificates/me`, { withCredentials: true })
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (certId, trackId) => {
    setDownloading(certId); setDownloadError('');
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/certificates/${certId}/download`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NHF-Certificate-${trackId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError("Couldn't download your certificate. Please try again.");
    }
    setDownloading('');
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3 mt-4"
      data-testid="my-certificates">
      <div className="flex items-center gap-2">
        <Award className="w-4 h-4 text-amber-500" />
        <h2 className="font-heading font-bold text-sm text-[#0A0A0A]">
          Your NHF Certificates
        </h2>
        <span className="text-[10px] text-[#9CA3AF] italic ml-auto">
          {rows.length} earned · Always downloadable
        </span>
      </div>
      {downloadError && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs p-2 rounded inline-flex items-start gap-2"
          data-testid="cert-download-error">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {downloadError}
        </div>
      )}
      <ul className="divide-y divide-[#F1F5F9]">
        {rows.map((c) => (
          <li key={c.cert_id} className="py-2.5 flex items-center gap-3"
            data-testid={`cert-row-${c.cert_id}`}>
            <span className="w-8 h-8 rounded bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
              <Award className="w-4 h-4 text-amber-600" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#0A0A0A]">{c.track_name}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5 flex items-center gap-2">
                <span>Issued {new Date(c.issued_at).toLocaleDateString()}</span>
                <span>·</span>
                <span className="font-mono">{c.cert_id.slice(0, 8)}…</span>
                {c.emailed && (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <Mail className="w-2.5 h-2.5" /> Emailed
                  </span>
                )}
              </p>
            </div>
            <button onClick={() => handleDownload(c.cert_id, c.track_id)}
              disabled={downloading === c.cert_id}
              className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 hover:bg-[#8B0F1C] transition-colors disabled:opacity-60"
              data-testid={`cert-download-${c.cert_id}`}>
              {downloading === c.cert_id
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Download className="w-3 h-3" />}
              Download PDF
            </button>
            {c.verification_url && (
              <a href={c.verification_url} target="_blank" rel="noopener noreferrer"
                className="text-[#4B5563] hover:text-[#BA1826] p-1.5"
                title="Verify"
                data-testid={`cert-verify-${c.cert_id}`}>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MyCertificates;
